import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { animate } from 'motion/react';
import CustomImage from '../../fragments/custom-image/CustomImage';

const TRANSITION = { duration: 0.5, ease: [0.45, 0, 0.55, 1] }; // ≈ quad in-out
const CLIP_VISIBLE = 'inset(0% 0 0 0)';
// The reveal runs top-to-bottom: hidden is collapsed against the top edge, and
// the exit keeps going the same way, collapsing against the bottom edge.
const CLIP_HIDDEN = 'inset(0% 0 100% 0)';
const CLIP_EXIT = 'inset(100% 0 0 0)';
// Parallax instead of a zoom: the image trails the clip's leading edge, sitting
// offset up while hidden, settling to 0 as the wipe comes down, then carrying
// on downward as it wipes out. The media is permanently scaled up in CSS so the
// offset never exposes an empty edge — keep PARALLAX_PCT below half that
// overscan.
const PARALLAX_PCT = 8;
const PARALLAX_FROM = `-${PARALLAX_PCT}%`;
const PARALLAX_TO = `${PARALLAX_PCT}%`;
const MEDIA_SELECTOR = '.NavigationFooter-hoverImageMedia';

const FooterHoverImage = forwardRef(function FooterHoverImage({ images }, ref) {
  const wrapperRefs = useRef([]); // one slot per global index (null where no image)
  const animationsRef = useRef([]); // in-flight AnimationPlaybackControls per slot
  const mediaAnimationsRef = useRef([]); // matching scale animation per slot
  const phaseRef = useRef([]); // 'entering' | 'visible' | 'exiting' | undefined (hidden)
  const activeRef = useRef(-1); // currently hovered image index
  const zIndexRef = useRef(0); // incoming image always stacks above the outgoing

  useEffect(() => {
    return () => {
      animationsRef.current.forEach((a) => a?.stop());
      mediaAnimationsRef.current.forEach((a) => a?.stop());
    };
  }, []);

  useImperativeHandle(ref, () => {
    const run = (i, keyframes, mediaKeyframes) => {
      const el = wrapperRefs.current[i];
      if (!el) return null;
      animationsRef.current[i]?.stop();
      animationsRef.current[i] = animate(el, keyframes, TRANSITION);

      const media = mediaKeyframes && el.querySelector(MEDIA_SELECTOR);
      if (media) {
        mediaAnimationsRef.current[i]?.stop();
        mediaAnimationsRef.current[i] = animate(
          media,
          mediaKeyframes,
          TRANSITION
        );
      }

      return animationsRef.current[i];
    };

    const exit = (index) => {
      phaseRef.current[index] = 'exiting';
      // Parallax continues on the way out. Single target (not a keyframe pair)
      // so an interrupted enter carries on from wherever the media currently is.
      const anim = run(index, { clipPath: CLIP_EXIT }, { y: PARALLAX_TO });
      anim?.finished
        .then(() => {
          if (animationsRef.current[index] !== anim) return; // superseded
          phaseRef.current[index] = undefined; // hidden
        })
        .catch(() => {});
    };

    const enter = (index) => {
      const el = wrapperRefs.current[index];
      el.style.zIndex = String(++zIndexRef.current);
      phaseRef.current[index] = 'entering';

      const anim = run(
        index,
        { clipPath: [CLIP_HIDDEN, CLIP_VISIBLE] },
        { y: [PARALLAX_FROM, 0] }
      );
      anim?.finished
        .then(() => {
          if (animationsRef.current[index] !== anim) return; // superseded
          phaseRef.current[index] = 'visible';
          // replaced (or nav left) while still entering — the reveal always
          // completes first, then the image wipes out the bottom
          if (activeRef.current !== index) exit(index);
        })
        .catch(() => {});
    };

    // request removal: exit now if fully visible; if still entering, the
    // enter callback chains the exit once the reveal completes
    const release = (index) => {
      if (phaseRef.current[index] === 'visible') exit(index);
    };

    const hide = () => {
      const current = activeRef.current;
      activeRef.current = -1;
      wrapperRefs.current.forEach((el, i) => {
        if (!el) return;
        if (i === current) release(i);
        else if (phaseRef.current[i] === 'visible') exit(i); // stray self-heal
      });
    };

    const show = (index) => {
      if (index === activeRef.current) return;
      const nextEl = wrapperRefs.current[index];
      if (!nextEl) {
        // link without a featured image
        hide();
        return;
      }

      const prev = activeRef.current;
      activeRef.current = index;

      // self-heal any stray left visible by a missed mouseleave
      wrapperRefs.current.forEach((el, i) => {
        if (
          el &&
          i !== index &&
          i !== prev &&
          phaseRef.current[i] === 'visible'
        ) {
          exit(i);
        }
      });

      if (prev !== -1) release(prev);
      enter(index);
    };

    return { show, hide };
  }, []);

  if (!images?.some(Boolean)) return null;

  return (
    <div className="NavigationFooter-hoverImage" aria-hidden="true">
      {images.map((img, i) =>
        img ? (
          <div
            key={img.id || i}
            className="NavigationFooter-hoverImageItem"
            ref={(el) => {
              wrapperRefs.current[i] = el;
            }}
          >
            <CustomImage
              image={img}
              fill
              className="NavigationFooter-hoverImageMedia"
              sizes="(min-width: 1024px) 33vw, 1vw"
            />
          </div>
        ) : null
      )}
    </div>
  );
});

export default FooterHoverImage;
